import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { CrmContact } from '@tips-crm/shared';
import { fetchAssignableUsers, fetchContacts, type ContactSort } from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column, type SortState } from '../components/DataTable.js';

const PAGE_SIZE = 50;

const LIFECYCLE_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'customer', label: 'Customer' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'churned', label: 'Churned' },
];

interface Filters {
  name: string;
  email: string;
  phone: string;
  company: string;
  lifecycle_status: string;
  // Index signature so this is assignable to DataTable's Record<string, string>.
  [key: string]: string;
}

const EMPTY_FILTERS: Filters = { name: '', email: '', phone: '', company: '', lifecycle_status: '' };

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function fullName(c: CrmContact): string {
  const parts = [c.first_name, c.last_name].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn btn--ghost back-arrow" onClick={onClick} aria-label="Go back">
      <span aria-hidden="true">←</span> Back
    </button>
  );
}

export function ContactsPage() {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { hasRole } = useAuth();
  const isAdmin = hasRole('platform_admin', 'reseller_admin');

  // Resolve assigned_user_id -> rep name for the Owner column (admins only).
  const usersQ = useQuery({
    queryKey: ['assignable-users'],
    queryFn: fetchAssignableUsers,
    enabled: isAdmin,
  });
  const ownerNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersQ.data ?? []) map.set(u.id, u.display_name);
    return map;
  }, [usersQ.data]);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState>({ key: 'updated_at', dir: 'desc' });
  const [page, setPage] = useState(0);

  const debouncedSearch = useDebounced(search);
  const debouncedFilters = useDebounced(filters);

  // Any change to the result set should send the user back to the first page,
  // otherwise they can land on an out-of-range offset showing nothing.
  useEffect(() => setPage(0), [debouncedSearch, debouncedFilters, sort]);

  const query = useQuery({
    queryKey: ['contacts', debouncedSearch, debouncedFilters, sort, page],
    queryFn: () =>
      fetchContacts({
        q: debouncedSearch || undefined,
        name: debouncedFilters.name || undefined,
        email: debouncedFilters.email || undefined,
        phone: debouncedFilters.phone || undefined,
        company: debouncedFilters.company || undefined,
        lifecycle_status: debouncedFilters.lifecycle_status || undefined,
        sort: sort.key as ContactSort,
        dir: sort.dir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData, // keep the current page visible while the next loads
  });

  const handleSort = (key: string) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );

  const handleFilter = (key: string, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const columns = useMemo<Column<CrmContact>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (c) => fullName(c),
        sortable: true,
        sortKey: 'name',
        filterable: true,
        filterPlaceholder: 'Filter name…',
      },
      {
        key: 'email',
        header: 'Email',
        render: (c) => c.email ?? '—',
        sortable: true,
        sortKey: 'email',
        filterable: true,
        filterPlaceholder: 'Filter email…',
      },
      {
        key: 'phone',
        header: 'Phone',
        render: (c) => c.phone_e164 ?? '—',
        sortable: true,
        sortKey: 'phone',
        filterable: true,
        filterPlaceholder: 'Filter phone…',
      },
      {
        key: 'company',
        header: 'Company',
        render: (c) => c.company ?? '—',
        sortable: true,
        sortKey: 'company',
        filterable: true,
        filterPlaceholder: 'Filter company…',
      },
      {
        key: 'lifecycle_status',
        header: 'Lifecycle',
        render: (c) =>
          c.lifecycle_status ? <span className="badge">{c.lifecycle_status}</span> : '—',
        sortable: true,
        sortKey: 'lifecycle_status',
        filterable: true,
        filterType: 'select',
        filterOptions: LIFECYCLE_OPTIONS,
      },
      // Owner column is admin-only: reps already only see their own allocated leads.
      ...(isAdmin
        ? [
            {
              key: 'owner',
              header: 'Owner',
              render: (c: CrmContact) =>
                c.assigned_user_id
                  ? ownerNames.get(c.assigned_user_id) ?? 'Assigned'
                  : <span className="badge">Unassigned</span>,
            } as Column<CrmContact>,
          ]
        : []),
    ],
    [isAdmin, ownerNames],
  );

  return (
    <div className="page">
      <PageHeader
        title="Contacts"
        subtitle="Customer records across your sellers."
        leading={<BackButton onClick={goBack} />}
        actions={<BackButton onClick={goBack} />}
      />

      <div className="toolbar">
        <input
          className="field__input search-input"
          type="search"
          placeholder="Search by name, email, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.items}
        rowKey={(c) => c.id}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => query.refetch()}
        onRowClick={(c) => navigate(`/contacts/${c.id}`)}
        emptyMessage="No contacts match your search."
        sort={sort}
        onSortChange={handleSort}
        filterValues={filters}
        onFilterChange={handleFilter}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: query.data?.total ?? 0,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
